import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  CreditCard,
  Cylinder as CylinderIcon,
  FileSignature,
  MessageSquare,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  Wrench,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useBubbly } from '@/components/bubbly/BubblyContext';
import { useCustomer } from '@/hooks/useCustomer';
import {
  SUPPORT_EMAIL,
  SUPPORT_MAILTO_HREF,
  SUPPORT_PHONE,
  SUPPORT_TEL_HREF,
} from '@/lib/constants';

/**
 * /guide — customer-facing "Getting Started" guide. A friendly tour of what
 * the portal does, with each card deep-linking to the real feature, plus a
 * prominent "Ask Bubbly" launcher (opens the app-wide Bubbly assistant via the
 * shared BubblyContext). Linked from the sidebar and from the welcome email.
 *
 * Only lists capabilities that work today — no promises of unbuilt features.
 */

interface GuideItem {
  icon: typeof ShoppingBag;
  title: string;
  body: string;
  to: string;
  cta: string;
}

const ITEMS: GuideItem[] = [
  {
    icon: ShoppingBag,
    title: 'Place an order',
    body: 'Browse the full Brix catalog, add what you need, and check out. Choose the delivery location and add notes for the driver.',
    to: '/order',
    cta: 'Start shopping',
  },
  {
    icon: Sparkles,
    title: 'Reorder your regulars',
    body: 'My Collection keeps the products you order most so you can rebuild a typical order in a couple of taps.',
    to: '/collection',
    cta: 'Open My Collection',
  },
  {
    icon: CreditCard,
    title: 'View & pay invoices',
    body: 'See open balances and past invoices, check what’s due, and pay online when a payment link is available.',
    to: '/invoices',
    cta: 'Go to Billing',
  },
  {
    icon: RefreshCw,
    title: 'Set up autopay',
    body: 'Turn on automatic payment so recurring invoices are handled without a manual step each cycle.',
    to: '/autopay',
    cta: 'Manage autopay',
  },
  {
    icon: Wrench,
    title: 'Request service',
    body: 'Something not pouring right? Open a service request and our team gets the details to schedule a visit.',
    to: '/service',
    cta: 'Request service',
  },
  {
    icon: CylinderIcon,
    title: 'Track cylinders & equipment',
    body: 'Keep tabs on the CO₂ cylinders and equipment installed at your locations.',
    to: '/cylinders',
    cta: 'View cylinders',
  },
  {
    icon: FileSignature,
    title: 'Your contracts & documents',
    body: 'Find your agreements and account documents stored in one place under your account.',
    to: '/account',
    cta: 'Open account',
  },
];

export default function Guide() {
  const { openBubbly } = useBubbly();
  const { data: ctx } = useCustomer();
  const customerName = ctx?.customer.name ?? null;

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      {/* Hero */}
      <div className="overflow-hidden rounded-2xl border border-brix-border bg-brix-surface">
        <div className="h-1.5 bg-brix-primary" />
        <div className="px-6 py-7 sm:px-8">
          <h1 className="text-2xl font-semibold tracking-tight text-brix-ink">
            Welcome to Brix Order{customerName ? `, ${customerName}` : ''}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brix-muted">
            Everything your account can do, in one place — order products, pay invoices, request
            service, and more. Here’s a quick tour. Anything you can’t find, just ask Bubbly.
          </p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            <Button type="button" size="md" onClick={() => openBubbly()}>
              <Bot size={15} /> Ask Bubbly
            </Button>
            <Link to="/order">
              <Button type="button" size="md" variant="secondary">
                <ShoppingBag size={15} /> Place an order
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Capability cards */}
      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ITEMS.map((item) => (
          <Link
            key={item.to + item.title}
            to={item.to}
            className="group flex flex-col rounded-xl border border-brix-border bg-brix-surface p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-brix-primary/40 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brix-primary-tint text-brix-primary">
                <item.icon size={18} />
              </span>
              <h2 className="text-sm font-semibold text-brix-ink">{item.title}</h2>
            </div>
            <p className="mt-2 flex-1 text-[13px] leading-relaxed text-brix-muted">{item.body}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-brix-primary">
              {item.cta}
              <ArrowRight
                size={14}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </span>
          </Link>
        ))}
      </div>

      {/* Bubbly + help */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-brix-border bg-brix-surface-alt/50">
        <div className="px-6 py-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brix-primary text-white">
              <Bot size={20} />
            </span>
            <div>
              <h2 className="text-base font-semibold text-brix-ink">Meet Bubbly, your assistant</h2>
              <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-brix-muted">
                Bubbly answers questions about ordering, delivery, billing, CO₂/tank swaps, and
                soda-machine troubleshooting — instantly, any time. You can also reach Bubbly from
                <strong className="text-brix-ink"> Contact us → Chat</strong> on any page.
              </p>
            </div>
          </div>
          <div className="mt-4 shrink-0 sm:mt-0">
            <Button type="button" size="md" onClick={() => openBubbly()}>
              <MessageSquare size={15} /> Ask Bubbly
            </Button>
          </div>
        </div>
        <div className="border-t border-brix-border px-6 py-4">
          <p className="text-[13px] text-brix-muted">
            Prefer a human? Email{' '}
            <a href={SUPPORT_MAILTO_HREF} className="font-semibold text-brix-primary hover:underline">
              {SUPPORT_EMAIL}
            </a>{' '}
            or call{' '}
            <a href={SUPPORT_TEL_HREF} className="font-semibold text-brix-primary hover:underline">
              {SUPPORT_PHONE}
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
